/**
 * Core vocabulary shared by the extension (enforcement plane) and the server
 * (control plane). Everything here is pure data — no browser or Node APIs.
 */

/** User actions the enforcement plane intercepts. Deliberately coarse: we act at
 * meaningful boundaries (paste, upload, submit…), never per keystroke. */
export const ACTIONS = ['navigate', 'paste', 'upload', 'download', 'submit'] as const;
export type Action = (typeof ACTIONS)[number];

/** Policy decisions, weakest → strongest. Order is significant: see DECISION_STRENGTH. */
export const DECISIONS = ['allow', 'log', 'warn', 'justify', 'block'] as const;
export type Decision = (typeof DECISIONS)[number];

export const DECISION_STRENGTH: Record<Decision, number> = {
  allow: 0,
  log: 1,
  warn: 2,
  justify: 3,
  block: 4,
};

export const ACCOUNT_TYPES = ['corporate', 'personal', 'unknown'] as const;
export type AccountType = (typeof ACCOUNT_TYPES)[number];

export const APP_CATEGORIES = [
  'genai',
  'cloud_storage',
  'webmail',
  'collaboration',
  'crm',
  'finance',
  'dev',
  'social',
  'file_transfer',
  'productivity',
  'unknown',
] as const;
export type AppCategory = (typeof APP_CATEGORIES)[number];

export const APP_CATEGORY_LABELS: Record<AppCategory, string> = {
  genai: 'Generative AI',
  cloud_storage: 'Cloud storage',
  webmail: 'Webmail',
  collaboration: 'Collaboration',
  crm: 'CRM',
  finance: 'Finance',
  dev: 'Source code & development',
  social: 'Social media',
  file_transfer: 'File transfer',
  productivity: 'Productivity',
  unknown: 'Unknown',
};

/** Administrator review status of an application. `unreviewed` is how the product
 * represents "unknown / not yet assessed" apps, including newly discovered AI tools. */
export const APP_APPROVALS = ['sanctioned', 'unsanctioned', 'unreviewed'] as const;
export type AppApproval = (typeof APP_APPROVALS)[number];

export const SENSITIVITY_LEVELS = ['public', 'internal', 'confidential', 'restricted'] as const;
export type Sensitivity = (typeof SENSITIVITY_LEVELS)[number];
export const SENSITIVITY_RANK: Record<Sensitivity, number> = {
  public: 0,
  internal: 1,
  confidential: 2,
  restricted: 3,
};

export const SEVERITIES = ['low', 'medium', 'high', 'critical'] as const;
export type Severity = (typeof SEVERITIES)[number];
export const SEVERITY_RANK: Record<Severity, number> = { low: 0, medium: 1, high: 2, critical: 3 };

/** Browsers the extension identifies itself as (policy rules can be scoped to any of them). */
export const BROWSERS = ['edge', 'chrome', 'firefox', 'safari', 'other'] as const;
export type BrowserKind = (typeof BROWSERS)[number];

/** Outcome recorded in telemetry. Distinct from Decision because the user's response
 * to a WARN / JUSTIFY prompt changes what actually happened. */
export const OUTCOMES = [
  'allowed',
  'logged',
  'warned_proceeded',
  'warned_cancelled',
  'justified',
  'justify_cancelled',
  'blocked',
] as const;
export type Outcome = (typeof OUTCOMES)[number];

export const PROTECTION_STATES = [
  'protected',
  'policy_stale',
  'extension_outdated',
  'disconnected',
  'unprotected',
  'unknown',
] as const;
export type ProtectionState = (typeof PROTECTION_STATES)[number];
